'use client';

import { Box, Typography } from '@mui/material';
import { usePathname } from 'next/navigation';
import theme from '@/theme';
import { colorVariables } from '@/constants';
import QAItem from './QAItem';
import { Question } from '@/lib/types/interfaces/Question';

type Props = {
  questions: Question[];
};

const replaceHyphensAndUnderscoresWithSpace = (str: string) => str.replace(/[-_]/g, ' ');

const formatText = (str: string) => {
  if (!str.length) {
    return '';
  }

  const formatted = replaceHyphensAndUnderscoresWithSpace(str)
    .replace(/\b\w/g, char => char.toUpperCase());

  const vowels = ['A', 'E', 'I', 'O', 'U'];
  const article = vowels.includes(formatted[0]) ? 'an' : 'a';

  return `${article} ${formatted}`;
};

const QuestionsAnswers = ({ questions }: Props) => {
  let category = '';

  const pathname = usePathname();
  const pathItems = pathname.split('/');

  if (pathname.includes('/services/')) {
    // eslint-disable-next-line prefer-destructuring
    category = pathItems[2];

    if (pathItems.length >= 4) {
      category = pathItems?.[3]?.length !== 2 ? pathItems[3] : category;
    }
  }

  const [firstColumn, secondColumn] = questions.reduce<[Question[], Question[]]>(
    (acc, question, index) => {
      acc[index % 2].push(question);
      return acc;
    },
    [[], []],
  );

  return (
    <Box>
      <Typography
        variant='h2'
        fontWeight={600}
        lineHeight='150%'
        fontSize={20}
        letterSpacing={0.15}
      >
        {`Things to consider when hiring ${formatText(category)} service provider:`}
      </Typography>

      <Box
        sx={{
          borderRadius: '16px',
          paddingX: 4,
          paddingY: 3,
          marginTop: 6,
          background: colorVariables.cardColor,
          [theme.breakpoints.down('lg')]: {
            marginTop: 3,
            paddingX: 3,
          },
        }}
      >
        <Box
          display='flex'
          gap={{ xs: 3, md: 6, lg: 6 }}
          flexDirection={{ xs: 'column', md: 'row', lg: 'row' }}
        >
          <Box
            display='flex'
            flexDirection='column'
            gap={{ xs: 3, md: 3, lg: 2 }}
            flexBasis='50%'
          >
            {firstColumn.map((item, index) => (
              <QAItem
                key={item.title}
                item={item}
                showBottomBorder={index !== firstColumn.length - 1}
              />
            ))}
          </Box>

          <Box
            display='flex'
            flexDirection='column'
            gap={{ xs: 3, md: 3, lg: 2 }}
            flexBasis='50%'
          >
            {secondColumn.map((item, index) => (
              <QAItem
                key={item.title}
                item={item}
                showBottomBorder={index !== secondColumn.length - 1 || firstColumn.length > secondColumn.length}
                isLastItem={index === secondColumn.length - 1}
              />
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default QuestionsAnswers;
